import { useState } from "react";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Checkbox,
} from "@mui/material";
import { delftBlue, flame } from "@/theme/colors";
import DateModal from "./date-modal";
import DownArrowIcon from "@/assets/mingcute_down-line.svg";
import { getFormSubmissionRef } from "@/utils/form-download";

const ViewIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.7305 11.2265C19.9768 11.5715 20.0999 11.7449 20.0999 12C20.0999 12.256 19.9768 12.4285 19.7305 12.7736C18.6241 14.3255 15.798 17.67 11.9999 17.67C8.201 17.67 5.37572 14.3247 4.26926 12.7736C4.02302 12.4285 3.8999 12.2552 3.8999 12C3.8999 11.7441 4.02302 11.5715 4.26926 11.2265C5.37572 9.67451 8.20181 6.33002 11.9999 6.33002C15.7988 6.33002 18.6241 9.67532 19.7305 11.2265Z"
      stroke={delftBlue[500]}
      strokeWidth="1.215"
    />
    <path
      d="M14.4299 12C14.4299 11.3555 14.1739 10.7375 13.7182 10.2817C13.2625 9.82603 12.6444 9.57002 11.9999 9.57002C11.3554 9.57002 10.7373 9.82603 10.2816 10.2817C9.82592 10.7375 9.5699 11.3555 9.5699 12C9.5699 12.6445 9.82592 13.2626 10.2816 13.7183C10.7373 14.174 11.3554 14.43 11.9999 14.43C12.6444 14.43 13.2625 14.174 13.7182 13.7183C14.1739 13.2626 14.4299 12.6445 14.4299 12Z"
      stroke={delftBlue[500]}
      strokeWidth="1.215"
    />
  </svg>
);

const AllFormsTable = ({
  submissions = [],
  onView,
  filters = {},
  onFilterChange,
  activities = [],
  isLoading = false,
  selected = {},
  onToggleOne,
  onTogglePage,
}) => {
  const theme = useTheme();
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dateState, setDateState] = useState([
    {
      startDate: filters.startDate ? new Date(filters.startDate) : new Date(),
      endDate: filters.endDate ? new Date(filters.endDate) : new Date(),
      key: "selection",
    },
  ]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDateToLocalString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (ranges) => {
    setDateState([ranges.selection]);
    // Don't close modal here - let user select both dates, then click Apply
  };

  const handleApplyDateFilter = () => {
    if (dateState[0]?.startDate && dateState[0]?.endDate) {
      onFilterChange({
        ...filters,
        startDate: formatDateToLocalString(dateState[0].startDate),
        endDate: formatDateToLocalString(dateState[0].endDate),
      });
      setDateModalOpen(false);
    }
  };

  const handleFilterChange = (key, value) => {
    onFilterChange({
      ...filters,
      [key]: value,
    });
  };

  const clearFilters = () => {
    onFilterChange({
      startDate: "",
      endDate: "",
      activityId: "",
      guideName: "",
      formType: "all",
    });
    setDateState([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
  };

  const pageRefs = submissions
    .map((submission) => getFormSubmissionRef(submission))
    .filter((ref) => ref.id);
  const selectedOnPageCount = pageRefs.filter((ref) => selected[ref.key]).length;
  const allOnPageSelected =
    pageRefs.length > 0 && selectedOnPageCount === pageRefs.length;
  const someOnPageSelected =
    selectedOnPageCount > 0 && selectedOnPageCount < pageRefs.length;

  return (
    <>
      {/* Filters Section */}
      <Box
        sx={{
          alignSelf: "stretch",
          padding: { xs: 2, sm: 2.5 },
          background: "white",
          borderRadius: 2,
          outline: `1px ${delftBlue[100]} solid`,
          outlineOffset: "-1px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{
            color: delftBlue[500],
            fontSize: { xs: 16, sm: 18 },
            fontFamily: "Audiowide, sans-serif",
            fontWeight: 400,
          }}
        >
          Filters
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}
        >
          {/* Date Range Filter */}
          <Button
            variant="outlined"
            onClick={() => setDateModalOpen(true)}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              height: 44,
              boxSizing: "border-box",
              padding: "0 16px",
              borderRadius: 2,
              borderColor: delftBlue[200],
              color: delftBlue[500],
              textTransform: "none",
              fontSize: { xs: 12, sm: 13 },
              fontFamily: "Montserrat, sans-serif",
              "&:hover": {
                borderColor: delftBlue[300],
                background: delftBlue[50],
              },
            }}
          >
            {filters.startDate && filters.endDate
              ? `${formatDate(filters.startDate)} - ${formatDate(
                filters.endDate
              )}`
              : "Select Date Range"}
          </Button>

          {/* Activity Filter */}
          {/* <FormControl
            sx={{
              minWidth: { xs: "100%", sm: 200 },
            }}
          >
            <InputLabel
              sx={{
                fontSize: { xs: 12, sm: 13 },
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              Activity
            </InputLabel>
            <Select
              value={filters.activityId || ""}
              onChange={(e) => handleFilterChange("activityId", e.target.value)}
              label="Activity"
              sx={{
                fontSize: { xs: 12, sm: 13 },
                fontFamily: "Montserrat, sans-serif",
                borderRadius: 2,
              }}
            >
              <MenuItem value="">All Activities</MenuItem>
              {activities.map((activity) => (
                <MenuItem key={activity._id || activity.id} value={activity._id || activity.id}>
                  {activity.name || "Unnamed Activity"}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}

          {/* Guide Name Filter */}
          <TextField
            value={filters.guideName || ""}
            onChange={(e) => handleFilterChange("guideName", e.target.value)}
            placeholder="User Name"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              "& .MuiOutlinedInput-root": {
                height: 44,
                boxSizing: "border-box",
                borderRadius: 2,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: delftBlue[200],
              },
              "& .MuiInputBase-input": {
                padding: "0 16px",
                height: 44,
                boxSizing: "border-box",
                fontSize: { xs: 12, sm: 13 },
                fontFamily: "Montserrat, sans-serif",
                "&::placeholder": {
                  color: delftBlue[500],
                  opacity: 1,
                },
              },
            }}
          />

          {/* Form Type Filter */}
          <FormControl
            sx={{
              minWidth: { xs: "100%", sm: 150 },
              height: 44,
            }}
          >
            <Select
              value={filters.formType || "all"}
              onChange={(e) => handleFilterChange("formType", e.target.value)}
              displayEmpty
              sx={{
                height: 44,
                boxSizing: "border-box",
                background: "white",
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: delftBlue[200],
                },
                "& .MuiSelect-select": {
                  padding: "0 32px 0 16px !important",
                  height: 44,
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  color: delftBlue[500],
                  fontSize: { xs: 12, sm: 13 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                },
              }}
              IconComponent={(props) => (
                <Box
                  component="img"
                  src={DownArrowIcon}
                  alt="Dropdown"
                  {...props}
                  sx={{
                    width: 24,
                    height: 24,
                    marginRight: 1,
                    pointerEvents: "auto",
                    cursor: "pointer",
                  }}
                />
              )}
              renderValue={(selected) => {
                if (!selected || selected === "all") {
                  return "Form Type";
                }
                const labels = {
                  kayak: "Kayak",
                  "surf-lesson": "Surf Lesson",
                  "first-aid": "First Aid",
                  "incident-report": "Incident Report",
                };
                return labels[selected] || selected;
              }}
            >
              <MenuItem value="all">All Forms</MenuItem>
              <MenuItem value="kayak">Kayak</MenuItem>
              <MenuItem value="surf-lesson">Surf Lesson</MenuItem>
              <MenuItem value="first-aid">First Aid</MenuItem>
              <MenuItem value="incident-report">Incident Report</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            onClick={clearFilters}
            sx={{
              minWidth: { xs: "100%", sm: 120 },
              height: 44,
              boxSizing: "border-box",
              padding: "0 16px",
              borderRadius: 2,
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              textTransform: "none",
              fontSize: { xs: 12, sm: 13 },
              fontFamily: "Montserrat, sans-serif",
              "&:hover": {
                borderColor: theme.palette.error.dark,
                background: theme.palette.error.light,
                color: "white",
              },
            }}
          >
            Clear Filters
          </Button>
        </Stack>
      </Box>

      {/* Table */}
      <Box
        sx={{
          alignSelf: "stretch",
          position: "relative",
          overflowX: "auto",
          overflowY: "visible",
          borderRadius: 2,
          outline: `1px ${delftBlue[100]} solid`,
          outlineOffset: "-1px",
          width: "100%",
          "&::-webkit-scrollbar": {
            height: 8,
          },
          "&::-webkit-scrollbar-track": {
            background: delftBlue[50],
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            background: delftBlue[200],
            borderRadius: 4,
            "&:hover": {
              background: delftBlue[300],
            },
          },
        }}
      >
        <Table
          sx={{
            minWidth: { xs: 800, sm: "100%" },
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: delftBlue[500],
                "& .MuiTableCell-root": {
                  backgroundColor: `${delftBlue[500]} !important`,
                },
              }}
            >
              <TableCell
                padding="checkbox"
                sx={{
                  height: { xs: 44, sm: 48 },
                  width: 48,
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                }}
              >
                <Checkbox
                  color="default"
                  checked={allOnPageSelected}
                  indeterminate={someOnPageSelected}
                  onChange={(event) =>
                    onTogglePage && onTogglePage(event.target.checked)
                  }
                  disabled={!pageRefs.length}
                  inputProps={{ "aria-label": "Select all forms on this page" }}
                  sx={{
                    color: "white",
                    "&.Mui-checked": { color: "white" },
                    "&.MuiCheckbox-indeterminate": { color: "white" },
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                  minWidth: { xs: 150, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Form Type
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                  minWidth: { xs: 150, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Activity
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                  minWidth: { xs: 150, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  User Name
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  borderRight: `1px ${theme.palette.neutral[200]} solid`,
                  borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                  minWidth: { xs: 120, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Submitted On
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  height: { xs: 44, sm: 48 },
                  paddingLeft: { xs: 1.5, sm: 2 },
                  paddingRight: { xs: 1.5, sm: 2 },
                  backgroundColor: `${delftBlue[500]} !important`,
                  textAlign: { xs: "left", sm: "center" },
                  minWidth: { xs: 100, sm: "auto" },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: 13,
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ padding: 4 }}>
                  <Typography
                    sx={{
                      color: theme.palette.neutral[600],
                      fontSize: 16,
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    {isLoading ? "Loading..." : "No form submissions found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              submissions.map((submission, index) => {
                const ref = getFormSubmissionRef(submission);
                const isChecked = Boolean(selected[ref.key]);
                const guideName =
                  submission.submittedBy?.fullName ||
                  submission.submittedBy?.username ||
                  submission.submittedBy?.email ||
                  "Unknown User";
                const activityName =
                  submission.activityId?.name ||
                  submission.activity?.name ||
                  "N/A";
                const formName =
                  submission.formName || submission.formType || "Unknown Form";

                return (
                  <TableRow
                    key={submission._id || submission.id || index}
                    sx={{
                      height: { xs: 50, sm: 40 },
                      background: index % 2 === 0 ? "white" : delftBlue[50],
                      borderBottom: `1px ${theme.palette.neutral[200]} solid`,
                      "&:hover": {
                        background: theme.palette.neutral[50],
                      },
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        borderRight: `1px ${theme.palette.neutral[200]} solid`,
                      }}
                    >
                      <Checkbox
                        color="primary"
                        checked={isChecked}
                        onChange={() => onToggleOne && onToggleOne(submission)}
                        onClick={(event) => event.stopPropagation()}
                        inputProps={{ "aria-label": `Select ${formName}` }}
                        sx={{
                          color: delftBlue[400],
                          "&.Mui-checked": { color: flame[500] },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingLeft: { xs: 1.5, sm: 2 },
                        paddingRight: { xs: 1.5, sm: 2 },
                        borderRight: `1px ${theme.palette.neutral[200]} solid`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.neutral[700],
                          fontSize: { xs: 14, sm: 15, md: 16 },
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 500,
                          wordBreak: "break-word",
                        }}
                      >
                        {formName}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingLeft: { xs: 1.5, sm: 2 },
                        paddingRight: { xs: 1.5, sm: 2 },
                        borderRight: `1px ${theme.palette.neutral[200]} solid`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.neutral[700],
                          fontSize: { xs: 14, sm: 15, md: 16 },
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 500,
                          wordBreak: "break-word",
                        }}
                      >
                        {activityName}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingLeft: { xs: 1.5, sm: 2 },
                        paddingRight: { xs: 1.5, sm: 2 },
                        borderRight: `1px ${theme.palette.neutral[200]} solid`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.neutral[700],
                          fontSize: { xs: 14, sm: 15, md: 16 },
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 500,
                          wordBreak: "break-word",
                        }}
                      >
                        {guideName}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingLeft: { xs: 1.5, sm: 2 },
                        paddingRight: { xs: 1.5, sm: 2 },
                        borderRight: `1px ${theme.palette.neutral[200]} solid`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.neutral[700],
                          fontSize: { xs: 14, sm: 15, md: 16 },
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        {formatDateTime(
                          submission.submittedAt || submission.createdAt
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingLeft: { xs: 1.5, sm: 2 },
                        paddingRight: { xs: 1.5, sm: 2 },
                        textAlign: { xs: "left", sm: "right" },
                      }}
                    >
                      <Box
                        onClick={() => onView && onView(submission)}
                        sx={{
                          paddingLeft: { xs: 0.75, sm: 1 },
                          paddingRight: { xs: 0.75, sm: 1 },
                          paddingTop: { xs: 0.5, sm: 0.5 },
                          paddingBottom: { xs: 0.5, sm: 0.5 },
                          background: "white",
                          borderRadius: 2,
                          outline: `1px ${theme.palette.neutral[200]} solid`,
                          outlineOffset: "-1px",
                          display: "inline-flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          touchAction: "manipulation",
                          minWidth: { xs: 60, sm: "auto" },
                          "&:hover": {
                            background: theme.palette.neutral[50],
                          },
                          "&:active": {
                            background: theme.palette.neutral[100],
                          },
                        }}
                      >
                        <ViewIcon />
                        <Typography
                          sx={{
                            color: delftBlue[500],
                            fontSize: { xs: 12, sm: 13 },
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          View
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Date Modal */}
      <DateModal
        open={dateModalOpen}
        handleClose={() => setDateModalOpen(false)}
        handleDateChange={handleDateChange}
        handleApply={handleApplyDateFilter}
        dateState={dateState}
      />
    </>
  );
};

export default AllFormsTable;
